var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { decomposeVector, getCursorOrder, wrapMouseMove, styled, px, } from './utils.js';
import * as zIndex from './zIndex.js';
import './block-resizer.js';
const dotSize = 20;
const halfSize = dotSize / 2;
const barWidth = 5;
const borderWidth = 2;
let ExEditorBlockCropperElement = class ExEditorBlockCropperElement extends LitElement {
    constructor() {
        super(...arguments);
        this.tmpTop = 0;
        this.tmpRight = 0;
        this.tmpBottom = 0;
        this.tmpLeft = 0;
        this.width = 0;
        this.height = 0;
        this.crop = {
            width: 0,
            height: 0,
            x: 0,
            y: 0,
        };
        this.rotate = 0;
        this.keepAspectRatio = false;
    }
    render() {
        var _a, _b, _c, _d;
        const cursorOrder = getCursorOrder(this.rotate);
        const containerStyle = styled({
            top: px(this.tmpTop),
            right: px(this.tmpRight),
            bottom: px(this.tmpBottom),
            left: px(this.tmpLeft),
        });
        const resizerStyle = styled({
            left: px(((_a = this.crop) === null || _a === void 0 ? void 0 : _a.x) || 0),
            top: px(((_b = this.crop) === null || _b === void 0 ? void 0 : _b.y) || 0),
            width: px(this.width || ((_c = this.crop) === null || _c === void 0 ? void 0 : _c.width)),
            height: px(this.height || ((_d = this.crop) === null || _d === void 0 ? void 0 : _d.height)),
        });
        return html `
      <div
        class="container"
        style="${containerStyle}"
        @mousedown=${this.handleMouseDown}
      >
        <div class="border"></div>

        <div
          draggable="true"
          data-x="-1"
          data-y="-1"
          class="dot top-left"
          style="cursor: ${cursorOrder[7]};"
        >
          <div class="handler top-bg"></div>
          <div class="handler left-bg"></div>
          <div class="handler top" style="left: 0;"></div>
          <div class="handler left"></div>
        </div>
        <div
          draggable="true"
          data-x="1"
          data-y="-1"
          class="dot top-right"
          style="cursor: ${cursorOrder[1]};"
        >
          <div class="handler top-bg"></div>
          <div class="handler right-bg"></div>
          <div class="handler top" style="right: 0;"></div>
          <div class="handler right"></div>
        </div>
        <div
          draggable="true"
          data-x="1"
          data-y="1"
          class="dot bottom-right"
          style="cursor: ${cursorOrder[3]};"
        >
          <div class="handler bottom-bg"></div>
          <div class="handler right-bg"></div>
          <div class="handler bottom" style="right: 0;"></div>
          <div class="handler right"></div>
        </div>
        <div
          draggable="true"
          data-x="-1"
          data-y="1"
          class="dot bottom-left"
          style="cursor: ${cursorOrder[5]};"
        >
          <div class="handler bottom-bg"></div>
          <div class="handler left-bg"></div>
          <div class="handler bottom" style="left: 0;"></div>
          <div class="handler left"></div>
        </div>

        <div
          draggable="true"
          data-x="0"
          data-y="-1"
          class="dot top"
          style="cursor: ${cursorOrder[0]};"
        >
          <div class="handler top-bg"></div>
          <div class="handler top"></div>
        </div>
        <div
          draggable="true"
          data-x="1"
          data-y="0"
          class="dot right"
          style="cursor: ${cursorOrder[2]};"
        >
          <div class="handler right-bg"></div>
          <div class="handler right"></div>
        </div>
        <div
          draggable="true"
          data-x="0"
          data-y="1"
          class="dot bottom"
          style="cursor: ${cursorOrder[4]};"
        >
          <div class="handler bottom-bg"></div>
          <div class="handler bottom"></div>
        </div>
        <div
          draggable="true"
          data-x="-1"
          data-y="0"
          class="dot left"
          style="cursor: ${cursorOrder[6]};"
        >
          <div class="handler left-bg"></div>
          <div class="handler left"></div>
        </div>
      </div>

      <slot class="copy" style="${resizerStyle}"></slot>

      <exeditor-block-resizer
        class="resizer"
        width=${this.width}
        height=${this.height}
        rotate=${this.rotate}
        min=${this.min}
        style="${resizerStyle}"
        ?keep-aspect-ratio=${this.keepAspectRatio}
        @mousedown=${this.handleImageMouseDown}
      ></exeditor-block-resizer>
    `;
    }
    handleMouseDown(originEvent) {
        const event = originEvent;
        const path = event.composed ? event.composedPath() : [];
        if (!path[0]) {
            return;
        }
        const target = path[0];
        if (!target.draggable) {
            return;
        }
        event.preventDefault();
        const horizontal = parseInt(target.dataset.x, 10);
        const vertical = parseInt(target.dataset.y, 10);
        const { x: x0, y: y0 } = event;
        const { min, max } = this;
        const crop = this.getCrop();
        const syncCropResult = () => {
            const x = this.tmpLeft;
            const y = this.tmpTop;
            const resultWidth = crop.width - this.tmpRight - this.tmpLeft;
            const resultHeight = crop.height - this.tmpTop - this.tmpBottom;
            this.tmpTop = 0;
            this.tmpRight = 0;
            this.tmpBottom = 0;
            this.tmpLeft = 0;
            const ev = new CustomEvent('customcrop', {
                composed: true,
                detail: {
                    width: resultWidth,
                    height: resultHeight,
                    x,
                    y,
                },
            });
            this.dispatchEvent(ev);
        };
        const handleMouseMove = (ev) => {
            const { x, y } = ev;
            let dx = x - x0;
            let dy = y - y0;
            const a = this.rotate || 0;
            const dvector = decomposeVector(dx, dy, a);
            dx = horizontal * dvector.x;
            dy = vertical * dvector.y;
            const dTop = vertical < 0 ? dy : 0;
            const dRight = horizontal > 0 ? dx : 0;
            const dBottom = vertical > 0 ? dy : 0;
            const dLeft = horizontal < 0 ? dx : 0;
            const minX = Math.max(dotSize, min);
            const minY = Math.max(dotSize, min);
            let maxX = max ? max : Infinity;
            let maxY = Infinity;
            const cos = Math.abs(Math.cos(a));
            const sin = Math.abs(Math.sin(a));
            maxX = (maxX - crop.height * sin) / cos;
            maxY = (maxY - crop.width * sin) / cos;
            this.tmpTop =
                dTop >= 0
                    ? Math.max(-dTop, crop.height - maxY)
                    : Math.min(-dTop, crop.height - minY);
            this.tmpRight =
                dRight >= 0
                    ? Math.max(-dRight, crop.width - maxX)
                    : Math.min(-dRight, crop.width - minX);
            this.tmpBottom =
                dBottom >= 0
                    ? Math.max(-dBottom, crop.height - maxY)
                    : Math.min(-dBottom, crop.height - minY);
            this.tmpLeft =
                dLeft >= 0
                    ? Math.max(-dLeft, crop.width - maxX)
                    : Math.min(-dLeft, crop.width - minX);
        };
        wrapMouseMove({
            onMove: handleMouseMove,
            onLeave: syncCropResult,
        });
    }
    handleImageMouseDown(originEvent) {
        const event = originEvent;
        const path = event.composed ? event.composedPath() : [];
        if (!path[0]) {
            return;
        }
        const target = path[0];
        if (target.className !== 'container') {
            return;
        }
        event.preventDefault();
        // event.stopPropagation();
        const { x: x0, y: y0 } = event;
        const crop = this.getCrop();
        let dx = 0;
        let dy = 0;
        const syncMoveResult = () => {
            const ev = new CustomEvent('customcrop', {
                detail: {
                    width: crop.width,
                    height: crop.height,
                    x: -dx,
                    y: -dy,
                },
            });
            this.dispatchEvent(ev);
        };
        const handleMouseMove = (ev) => {
            const { x, y } = ev;
            dx = x - x0;
            dy = y - y0;
            const dvector = decomposeVector(dx, dy, this.rotate || 0);
            dx = dvector.x;
            dy = dvector.y;
            const left = crop.x + dx;
            const top = crop.y + dy;
            const style = {
                left: px(left),
                top: px(top),
            };
            Object.assign(this.resizer.style, style);
            Object.assign(this.copy.style, style);
            const e = new CustomEvent('custommove', {
                detail: {
                    left,
                    top,
                },
            });
            this.dispatchEvent(e);
        };
        wrapMouseMove({
            onMove: handleMouseMove,
            onLeave: syncMoveResult,
        });
    }
    getCrop() {
        let { crop } = this;
        // 创建新的裁剪区域
        if (!crop) {
            crop = {
                width: this.width,
                height: this.height,
                x: 0,
                y: 0,
            };
            this.crop = crop;
        }
        return crop;
    }
};
ExEditorBlockCropperElement.styles = [
    css `
      :host {
        position: relative;
        display: block;
      }
      .container {
        position: absolute;
        z-index: ${zIndex.cropBox};
      }

      .resizer {
        display: block;
        position: absolute;
        cursor: move;
      }

      .copy {
        display: block;
        position: absolute;
        width: 100%;
        height: 100%;
        opacity: 0.5;
      }

      .border {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        border: ${barWidth}px solid #0003;
        pointer-events: none;
      }

      .dot {
        box-sizing: border-box;
        position: absolute;
        width: ${dotSize}px;
        height: ${dotSize}px;
        z-index: ${zIndex.cropDot};
      }
      .dot.top {
        top: 0px;
        left: calc(50% - ${halfSize}px);
      }
      .dot.right {
        right: 0px;
        top: calc(50% - ${halfSize}px);
      }
      .dot.bottom {
        bottom: 0px;
        left: calc(50% - ${halfSize}px);
      }
      .dot.left {
        left: 0px;
        top: calc(50% - ${halfSize}px);
      }
      .dot.top-right {
        top: 0px;
        right: 0px;
      }
      .dot.top-left {
        top: 0px;
        left: 0px;
      }
      .dot.bottom-right {
        bottom: 0px;
        right: 0px;
      }
      .dot.bottom-left {
        bottom: 0px;
        left: 0px;
      }

      .handler {
        position: absolute;
        pointer-events: none;
      }
      .handler.top,
      .handler.top-bg {
        top: 0;
      }
      .handler.bottom,
      .handler.bottom-bg {
        bottom: 0;
      }
      .handler.top,
      .handler.top-bg,
      .handler.bottom,
      .handler.bottom-bg {
        left: 0;
        right: 0;
        height: ${barWidth}px;
      }
      .handler.top,
      .handler.bottom {
        left: ${borderWidth}px;
        right: ${borderWidth}px;
        height: ${barWidth - borderWidth}px;
      }
      .handler.right,
      .handler.right-bg {
        right: 0;
      }
      .handler.left,
      .handler.left-bg {
        left: 0;
      }
      .handler.right,
      .handler.right-bg,
      .handler.left,
      .handler.left-bg {
        top: 0;
        bottom: 0;
        width: ${barWidth}px;
      }
      .handler.right,
      .handler.left {
        top: ${borderWidth}px;
        bottom: ${borderWidth}px;
        width: ${barWidth - borderWidth}px;
      }

      .handler.top,
      .handler.right,
      .handler.bottom,
      .handler.left {
        background-color: #000;
      }

      .handler.top-bg,
      .handler.right-bg,
      .handler.bottom-bg,
      .handler.left-bg {
        background-color: #fff;
      }
    `,
];
__decorate([
    query('.resizer')
], ExEditorBlockCropperElement.prototype, "resizer", void 0);
__decorate([
    query('.copy')
], ExEditorBlockCropperElement.prototype, "copy", void 0);
__decorate([
    state()
], ExEditorBlockCropperElement.prototype, "tmpTop", void 0);
__decorate([
    state()
], ExEditorBlockCropperElement.prototype, "tmpRight", void 0);
__decorate([
    state()
], ExEditorBlockCropperElement.prototype, "tmpBottom", void 0);
__decorate([
    state()
], ExEditorBlockCropperElement.prototype, "tmpLeft", void 0);
__decorate([
    property({
        attribute: true,
    })
], ExEditorBlockCropperElement.prototype, "src", void 0);
__decorate([
    property({
        attribute: true,
        type: Number,
    })
], ExEditorBlockCropperElement.prototype, "min", void 0);
__decorate([
    property({
        attribute: true,
        type: Number,
    })
], ExEditorBlockCropperElement.prototype, "max", void 0);
__decorate([
    property({
        attribute: true,
        reflect: true,
        type: Number,
    })
], ExEditorBlockCropperElement.prototype, "width", void 0);
__decorate([
    property({
        attribute: true,
        reflect: true,
        type: Number,
    })
], ExEditorBlockCropperElement.prototype, "height", void 0);
__decorate([
    property({
        attribute: true,
        reflect: true,
        type: Object,
    })
], ExEditorBlockCropperElement.prototype, "crop", void 0);
__decorate([
    property({
        reflect: true,
        type: Number,
    })
], ExEditorBlockCropperElement.prototype, "rotate", void 0);
__decorate([
    property({
        attribute: 'keep-aspect-ratio',
        type: Boolean,
    })
], ExEditorBlockCropperElement.prototype, "keepAspectRatio", void 0);
ExEditorBlockCropperElement = __decorate([
    customElement('exeditor-block-cropper')
], ExEditorBlockCropperElement);
export { ExEditorBlockCropperElement };
//# sourceMappingURL=block-cropper.js.map